import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const ConfigChatWidgets = () => {
  const { API, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    chat_activo: null,
    tidio_script: '//code.tidio.co/a1meggaz8qfjmotwlkqlnc2sc3bcy28t.js',
    elevenlabs_agent_id: 'agent_3201kckfz407fxxv2bek695fjb0b'
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/config-chat-widgets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig({
        chat_activo: response.data.chat_activo || null,
        tidio_script: response.data.tidio_script || '//code.tidio.co/a1meggaz8qfjmotwlkqlnc2sc3bcy28t.js',
        elevenlabs_agent_id: response.data.elevenlabs_agent_id || 'agent_3201kckfz407fxxv2bek695fjb0b'
      });
    } catch (error) {
      console.error('Error loading chat config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/config-chat-widgets`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada. Los cambios se aplicarán cuando los usuarios recarguen la página.');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectChat = (chatType) => {
    if (config.chat_activo === chatType) {
      // Si ya está seleccionado, desactivar
      setConfig({ ...config, chat_activo: null });
    } else {
      // Activar el seleccionado
      setConfig({ ...config, chat_activo: chatType });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <i className="fas fa-spinner fa-spin text-2xl text-blue-600"></i>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-3">
            <i className="fas fa-comments text-2xl"></i>
            <div>
              <h2 className="text-xl font-bold">Configuración de Widgets de Chat</h2>
              <p className="text-purple-100 text-sm font-normal mt-1">
                Elige qué chat mostrar a los usuarios o desactiva ambos
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Alerta informativa */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Solo puede estar activo un chat a la vez</p>
                <p className="mt-1 text-blue-600">
                  Los cambios se aplicarán cuando los usuarios recarguen la página o inicien sesión nuevamente.
                </p>
              </div>
            </div>
          </div>

          {/* Estado actual */}
          <div className="bg-gray-50 rounded-lg p-4 border">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Estado actual:</span>{' '}
              {config.chat_activo === 'tidio' && (
                <span className="text-green-600 font-medium">
                  <i className="fas fa-check-circle mr-1"></i>Tidio activo
                </span>
              )}
              {config.chat_activo === 'elevenlabs' && (
                <span className="text-purple-600 font-medium">
                  <i className="fas fa-check-circle mr-1"></i>ElevenLabs activo
                </span>
              )}
              {!config.chat_activo && (
                <span className="text-gray-500 font-medium">
                  <i className="fas fa-times-circle mr-1"></i>Ningún chat activo
                </span>
              )}
            </p>
          </div>

          {/* Opción Tidio */}
          <div 
            className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
              config.chat_activo === 'tidio' 
                ? 'border-green-500 bg-green-50 shadow-md' 
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => handleSelectChat('tidio')}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  config.chat_activo === 'tidio' ? 'bg-green-500' : 'bg-gray-200'
                }`}>
                  <i className={`fas fa-comment-dots text-xl ${
                    config.chat_activo === 'tidio' ? 'text-white' : 'text-gray-500'
                  }`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">Tidio</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Chat en vivo para atención a visitantes y usuarios
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      Chat en vivo
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      Chatbot
                    </span>
                  </div>
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                config.chat_activo === 'tidio' 
                  ? 'border-green-500 bg-green-500' 
                  : 'border-gray-300'
              }`}>
                {config.chat_activo === 'tidio' && (
                  <i className="fas fa-check text-white text-xs"></i>
                )}
              </div>
            </div>
            
            {config.chat_activo === 'tidio' && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <Label className="text-sm text-gray-600">Script URL</Label>
                <Input
                  value={config.tidio_script}
                  onChange={(e) => setConfig({ ...config, tidio_script: e.target.value })}
                  placeholder="//code.tidio.co/..."
                  className="mt-1"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>

          {/* Opción ElevenLabs */}
          <div 
            className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
              config.chat_activo === 'elevenlabs' 
                ? 'border-purple-500 bg-purple-50 shadow-md' 
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => handleSelectChat('elevenlabs')}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  config.chat_activo === 'elevenlabs' ? 'bg-purple-500' : 'bg-gray-200'
                }`}>
                  <i className={`fas fa-robot text-xl ${
                    config.chat_activo === 'elevenlabs' ? 'text-white' : 'text-gray-500'
                  }`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">ElevenLabs AI</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Asistente de voz con inteligencia artificial
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      IA Conversacional
                    </span>
                    <span className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded">
                      Voz
                    </span>
                  </div>
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                config.chat_activo === 'elevenlabs' 
                  ? 'border-purple-500 bg-purple-500' 
                  : 'border-gray-300'
              }`}>
                {config.chat_activo === 'elevenlabs' && (
                  <i className="fas fa-check text-white text-xs"></i>
                )}
              </div>
            </div>
            
            {config.chat_activo === 'elevenlabs' && (
              <div className="mt-4 pt-4 border-t border-purple-200">
                <Label className="text-sm text-gray-600">Agent ID</Label>
                <Input
                  value={config.elevenlabs_agent_id}
                  onChange={(e) => setConfig({ ...config, elevenlabs_agent_id: e.target.value })}
                  placeholder="agent_..."
                  className="mt-1"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>

          {/* Opción Desactivar ambos */}
          <div 
            className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
              config.chat_activo === null 
                ? 'border-gray-500 bg-gray-50 shadow-md' 
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => setConfig({ ...config, chat_activo: null })}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  config.chat_activo === null ? 'bg-gray-500' : 'bg-gray-200'
                }`}>
                  <i className={`fas fa-power-off text-xl ${
                    config.chat_activo === null ? 'text-white' : 'text-gray-500'
                  }`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">Desactivar Chats</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    No mostrar ningún widget de chat a los usuarios
                  </p>
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                config.chat_activo === null 
                  ? 'border-gray-500 bg-gray-500' 
                  : 'border-gray-300'
              }`}>
                {config.chat_activo === null && (
                  <i className="fas fa-check text-white text-xs"></i>
                )}
              </div>
            </div>
          </div>

          {/* Botón Guardar */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8"
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Guardar Configuración
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfigChatWidgets;
